import { useEffect, useRef, useCallback } from 'react';
import {
  getLockStatus,
  acquireLock,
  releaseLock,
  CampaignLockedError
} from '../api/campaignLock';
import { BASE_URL } from '../api/constants';
import {
  useCampaignContextController,
  setCampaignLock,
  setLockLoading
} from '../context/CampaignContext';
import { useSnackbar } from '../context/SnackbarContext';

const LOCK_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Manages campaign lock lifecycle: acquire on mount when user can edit,
 * release on unmount, poll to extend/check status. Updates CampaignContext.
 */
export function useCampaignLock(
  campaignId: string | undefined,
  currentUserName: string
) {
  const [state, dispatch] = useCampaignContextController();
  const { showSnackbar } = useSnackbar();
  const { canEdit } = state;
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const tryAcquireLock = useCallback(() => {
    if (!campaignId) return Promise.resolve(false);
    return acquireLock(campaignId)
      .then(() => {
        setCampaignLock(dispatch, { canEdit: true, lockedByUserName: null });
        return true;
      })
      .catch((err: unknown) => {
        if (err instanceof CampaignLockedError) {
          return getLockStatus(campaignId).then((status) => {
            setCampaignLock(dispatch, {
              canEdit: false,
              lockedByUserName: status.lock.locked_by_user_name
            });
            return false;
          });
        }
        throw err;
      });
  }, [campaignId, dispatch]);

  const releaseLockOnUnmount = useCallback(() => {
    if (!campaignId || !canEdit) return Promise.resolve();
    return releaseLock(campaignId).catch(() => {
      // Best effort; page is leaving
    });
  }, [campaignId, canEdit]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial lock check and acquire when campaign and user are ready
  useEffect(() => {
    if (!campaignId || !currentUserName) return;

    let cancelled = false;

    setLockLoading(dispatch, true);
    getLockStatus(campaignId)
      .then(({ lock }) => {
        if (cancelled) return;
        if (lock.is_locked && lock.locked_by_user_name !== currentUserName) {
          setCampaignLock(dispatch, {
            canEdit: false,
            lockedByUserName: lock.locked_by_user_name
          });
          return;
        }
        return acquireLock(campaignId)
          .then(() => {
            if (cancelled) return;
            setCampaignLock(dispatch, {
              canEdit: true,
              lockedByUserName: null
            });
          })
          .catch((err: unknown) => {
            if (err instanceof CampaignLockedError) {
              return getLockStatus(campaignId).then((status) => {
                if (cancelled) return;
                setCampaignLock(dispatch, {
                  canEdit: false,
                  lockedByUserName: status.lock.locked_by_user_name
                });
              });
            }
            showSnackbar('Failed to acquire edit lock', 'error');
            setCampaignLock(dispatch, {
              canEdit: false,
              lockedByUserName: null
            });
          });
      })
      .catch(() => {
        if (!cancelled) {
          showSnackbar('Failed to check campaign lock status', 'error');
          setCampaignLock(dispatch, { canEdit: false, lockedByUserName: null });
        }
      })
      .finally(() => {
        if (!cancelled) setLockLoading(dispatch, false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, currentUserName, dispatch, showSnackbar]);

  // Poll to extend lock (when we hold it) or check if released (when read-only)
  useEffect(() => {
    if (!campaignId || !currentUserName) return;

    const poll = () => {
      if (!isMountedRef.current) return;
      getLockStatus(campaignId)
        .then(({ lock }) => {
          if (!isMountedRef.current) return;
          if (lock.is_locked && lock.locked_by_user_name !== currentUserName) {
            setCampaignLock(dispatch, {
              canEdit: false,
              lockedByUserName: lock.locked_by_user_name
            });
            return;
          }
          if (canEdit) {
            return acquireLock(campaignId).then(() => {
              if (isMountedRef.current) {
                setCampaignLock(dispatch, {
                  canEdit: true,
                  lockedByUserName: null
                });
              }
            });
          }
          return acquireLock(campaignId)
            .then(() => {
              if (isMountedRef.current) {
                setCampaignLock(dispatch, {
                  canEdit: true,
                  lockedByUserName: null
                });
              }
            })
            .catch(() => {
              // Still locked, no change
            });
        })
        .catch(() => {
          // Ignore poll errors
        });
    };

    pollIntervalRef.current = setInterval(poll, LOCK_POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [campaignId, currentUserName, canEdit, dispatch]);

  // Release on unmount
  useEffect(() => {
    return () => {
      releaseLockOnUnmount();
    };
  }, [releaseLockOnUnmount]);

  // Release on beforeunload (tab close, navigation) - fire-and-forget with keepalive
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!campaignId || !canEdit) return;
      const token = document.cookie
        .split('; ')
        .find((row) => row.startsWith('user_session_token='))
        ?.split('=')[1];
      if (token) {
        fetch(`${BASE_URL}/v1/campaigns/${campaignId}/lock`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true
        }).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [campaignId, canEdit]);

  return { tryAcquireLock, releaseLockOnUnmount };
}
